import { getDateTime } from '@mews-commander/utils';
import { TimeUnit } from '@mews/enums';
import { DateService, DateTime, DateTimeLocalizationInfo, DateTimeService, lt, TimeService } from '@mews/framework';
import { StructuredDate } from '@optimus-web/core';
import DateStringPickerField from './DateStringPickerField';
import DateTimePickerField from './DateTimePickerField';
import MonthStringPickerField from './MonthStringPickerField';

export type TimeUnitFieldValue = string | { date: string; time: string };

export const parseTimeUnitFieldValue = (
    value: TimeUnitFieldValue,
    localizationInfo: DateTimeLocalizationInfo,
    timezone: string
) => {
    if (typeof value === 'object') {
        const { date, time } = value;
        return getDateTime({ localizedDate: date, localizedTime: time, timezone, localizationInfo });
    }

    // Monthly picker is using localized dates as its backend format.
    return DateTimeService.fromDate(DateService.fromString(value, localizationInfo, timezone));
};
export const createTimeUnitFieldInitialValue = (
    value: DateTime,
    timeUnit: TimeUnit,
    localizationInfo: DateTimeLocalizationInfo
): TimeUnitFieldValue => {
    if (timeUnit === TimeUnit.Hour) {
        return {
            date: DateService.toString(DateTimeService.toDate(value), localizationInfo),
            time: TimeService.toShortString(DateTimeService.toTime(value), localizationInfo),
        };
    }
    return DateService.toString(DateTimeService.toDate(value), localizationInfo);
};
export const parseFormTimeUnitDateString = (value: TimeUnitFieldValue | null | undefined) => {
    if (value == null) return undefined;
    if (typeof value === 'object') return value.date;
    return value;
};

interface TimeUnitIntervalPickerFieldsProps {
    startName: string;
    endName: string;
    timeUnit: TimeUnit;
    startTimeName?: string;
    endTimeName?: string;
    startMinDate?: StructuredDate | null;
    startMaxDate?: StructuredDate | null;
    endMinDate?: StructuredDate | null;
    endMaxDate?: StructuredDate | null;
    required?: boolean;
    disabled?: boolean;
    form: string;
}

interface TimeUnitField {
    timeUnit: TimeUnit;
    name: string;
    label: React.ReactNode;
    minDate?: StructuredDate | null;
    maxDate?: StructuredDate | null;
    form: string;
    required?: boolean;
    disabled?: boolean;
}
const field = ({ timeUnit, ...props }: TimeUnitField) => {
    switch (timeUnit) {
        case TimeUnit.Month:
            return <MonthStringPickerField {...props} />;
        case TimeUnit.Hour:
            return <DateTimePickerField {...props} allowDateReset />;
        default:
            return <DateStringPickerField {...props} required />;
    }
};

export const TimeUnitIntervalPickerFields = ({
    startName,
    endName,
    startMinDate,
    startMaxDate,
    endMinDate,
    endMaxDate,
    ...other
}: TimeUnitIntervalPickerFieldsProps) => (
    <>
        {field({
            label: lt('Arrival'),
            name: startName,
            minDate: startMinDate,
            maxDate: startMaxDate,
            ...other,
        })}
        {field({
            label: lt('Departure'),
            name: endName,
            minDate: endMinDate,
            maxDate: endMaxDate,
            ...other,
        })}
    </>
);
